import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Bookmark, Briefcase, CheckCircle2, FileText, GraduationCap, ShieldCheck, XCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import { Textarea } from "@/components/ui/textarea";
import { EmptyState, LoadingGrid, StatCard, StatusBadge } from "@/components/bits";
import { AiJobMatches } from "@/components/AiJobMatches";
import { NotificationPrefs } from "@/components/NotificationPrefs";
import api, { errMsg, fmtDate } from "@/lib/api";

export default function CandidateDashboard() {
  const [data, setData] = useState(null);
  const [requests, setRequests] = useState([]);
  const [bgvList, setBgvList] = useState([]);
  const [docs, setDocs] = useState([]);
  const [savedJobs, setSavedJobs] = useState([]);
  const [alerts, setAlerts] = useState({ keywords: "", location: "", enabled: false });
  const [viewDoc, setViewDoc] = useState(null);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState(null);

  const load = () => {
    api.get("/candidate/overview").then(({ data }) => setData(data)).catch(() => setData({}));
    api.get("/contact-requests/mine").then(({ data }) => setRequests(data.requests)).catch(() => {});
    api.get("/bgv/mine").then(({ data }) => setBgvList(data.bgv)).catch(() => {});
    api.get("/documents/mine").then(({ data }) => setDocs(data.documents)).catch(() => {});
    api.get("/candidate/saved-jobs").then(({ data }) => setSavedJobs(data.saved_jobs)).catch(() => {});
    api.get("/candidate/job-alerts").then(({ data }) => setAlerts(data.alerts)).catch(() => {});
  };
  useEffect(() => {
    load();
  }, []);

  if (!data) return <LoadingGrid count={4} />;

  const p = data.profile || {};

  const startEdit = () => {
    setForm({
      headline: p.headline || "", skills: (p.skills || []).join(", "),
      experience_years: p.experience_years || 0, qualification: p.qualification || "",
      location: p.location || "", expected_ctc: p.expected_ctc || "", current_ctc: p.current_ctc || "",
      notice_period: p.notice_period || "", summary: p.summary || "", phone: p.phone || "",
      industry: p.industry || "",
    });
    setEditing(true);
  };

  const saveProfile = async () => {
    try {
      await api.put("/candidate/profile", {
        ...form,
        skills: form.skills.split(",").map((s) => s.trim()).filter(Boolean),
        experience_years: Number(form.experience_years),
        expected_ctc: Number(form.expected_ctc || 0),
        current_ctc: Number(form.current_ctc || 0),
      });
      toast.success("Profile updated");
      setEditing(false);
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const respond = async (id, action) => {
    try {
      await api.post(`/contact-requests/${id}/respond`, { action });
      toast.success(`Request ${action}ed`);
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const consentBgv = async (id, consent) => {
    try {
      await api.post(`/bgv/${id}/consent`, { consent, details: {} });
      toast.success(consent ? "Consent given — verification started" : "Consent declined");
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  return (
    <div data-testid="candidate-dashboard">
      <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard label="Applications" value={data.applications?.length ?? 0} icon={FileText} />
        <StatCard label="In Interview" value={data.applications?.filter((a) => a.stage.includes("interview")).length ?? 0} icon={Briefcase} />
        <StatCard label="Offers / Joined" value={data.applications?.filter((a) => ["offer", "joined"].includes(a.stage)).length ?? 0} icon={CheckCircle2} accent />
        <StatCard label="Courses" value={data.enrollments?.length ?? 0} icon={GraduationCap} />
      </div>

      <div className="grid lg:grid-cols-3 gap-6 mt-8">
        <div className="lg:col-span-2 space-y-6">
          {bgvList.filter((b) => b.status === "initiated").length > 0 && (
            <div className="bg-white border border-cyan-300 rounded-xl p-6" data-testid="bgv-consent-card">
              <h3 className="font-heading font-semibold text-[#0B1B3D] mb-4 flex items-center gap-2">
                <ShieldCheck className="w-4 h-4 text-cyan-600" /> Background verification consent
              </h3>
              {bgvList.filter((b) => b.status === "initiated").map((b) => (
                <div key={b.id} className="border border-border/60 rounded-lg p-4 mb-3" data-testid={`bgv-consent-${b.id}`}>
                  <p className="text-sm">
                    <span className="font-semibold">{b.job_title}</span>{" "}
                    <span className="text-muted-foreground">— background verification ({b.checks.map((c) => c.name).join(", ")}) has been initiated. Your consent is required to proceed.</span>
                  </p>
                  <div className="flex gap-2 mt-3">
                    <Button size="sm" className="rounded-full bg-emerald-600 hover:bg-emerald-700" onClick={() => consentBgv(b.id, true)} data-testid={`bgv-accept-${b.id}`}>
                      I Consent
                    </Button>
                    <Button size="sm" variant="outline" className="rounded-full text-red-600 border-red-200" onClick={() => consentBgv(b.id, false)} data-testid={`bgv-decline-${b.id}`}>
                      Decline
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
          {bgvList.filter((b) => ["in_verification", "completed"].includes(b.status)).length > 0 && (
            <div className="bg-white border border-border/60 rounded-xl p-6" data-testid="bgv-status-card">
              <h3 className="font-heading font-semibold text-[#0B1B3D] mb-4">Background verification status</h3>
              {bgvList.filter((b) => ["in_verification", "completed"].includes(b.status)).map((b) => (
                <div key={b.id} className="flex items-center justify-between border border-border/50 rounded-lg p-4 mb-2" data-testid={`bgv-status-${b.id}`}>
                  <p className="text-sm font-medium">{b.job_title}</p>
                  <StatusBadge status={b.status} />
                </div>
              ))}
            </div>
          )}
          {requests.filter((r) => r.status === "pending").length > 0 && (
            <div className="bg-white border border-[#F5A623]/40 rounded-xl p-6" data-testid="contact-requests-card">
              <h3 className="font-heading font-semibold text-[#0B1B3D] mb-4">Contact requests</h3>
              {requests.filter((r) => r.status === "pending").map((r) => (
                <div key={r.id} className="border border-border/60 rounded-lg p-4 flex flex-wrap items-center justify-between gap-3 mb-3" data-testid={`contact-request-${r.id}`}>
                  <p className="text-sm">
                    <span className="font-semibold">{r.company_name}</span>{" "}
                    <span className="text-muted-foreground">wants to contact you regarding {r.job_context || "an opportunity"}.</span>
                  </p>
                  <div className="flex gap-2">
                    <Button size="sm" className="rounded-full bg-emerald-600 hover:bg-emerald-700" onClick={() => respond(r.id, "accept")} data-testid={`accept-contact-${r.id}`}>
                      Accept
                    </Button>
                    <Button size="sm" variant="outline" className="rounded-full text-red-600 border-red-200" onClick={() => respond(r.id, "decline")} data-testid={`decline-contact-${r.id}`}>
                      Decline
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className="bg-white border border-border/60 rounded-xl p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading font-semibold text-[#0B1B3D]">Recent applications</h3>
              <Link to="/candidate/applications" className="text-sm text-[#2563EB] font-medium">View all</Link>
            </div>
            {data.applications?.length === 0 ? (
              <EmptyState title="No applications yet." description="Explore open roles and apply in one click." cta="Explore Jobs" onCta={() => (window.location.href = "/jobs")} testId="apps-empty" />
            ) : (
              <div className="space-y-3">
                {data.applications.slice(0, 5).map((a) => (
                  <div key={a.id} className="flex items-center justify-between border border-border/50 rounded-lg p-4" data-testid={`my-app-${a.id}`}>
                    <div>
                      <p className="font-semibold text-sm text-[#0B1B3D]">{a.job_title}</p>
                      <p className="text-xs text-muted-foreground">{a.company_name} · {fmtDate(a.created_at)}</p>
                    </div>
                    <StatusBadge status={a.stage} />
                  </div>
                ))}
              </div>
            )}
          </div>

          <AiJobMatches />
        </div>

        <div className="space-y-6">
          <div className="bg-white border border-border/60 rounded-xl p-6" data-testid="profile-card">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-heading font-semibold text-[#0B1B3D]">Profile strength</h3>
              <span className="font-heading font-bold text-[#2563EB]">{data.profile_strength}%</span>
            </div>
            <Progress value={data.profile_strength} className="h-2" />
            {!editing ? (
              <>
                <div className="mt-5 text-sm space-y-2">
                  <p className="font-semibold">{p.headline || "Add a headline"}</p>
                  <p className="text-xs text-muted-foreground">{p.location || "Add location"} · {p.experience_years || 0} yrs</p>
                  <div className="flex gap-1.5 flex-wrap">
                    {(p.skills || []).slice(0, 6).map((s) => (
                      <span key={s} className="text-[11px] bg-[#2563EB]/8 text-[#2563EB] rounded-full px-2 py-0.5">{s}</span>
                    ))}
                  </div>
                </div>
                <Button variant="outline" size="sm" className="mt-5 rounded-full w-full" onClick={startEdit} data-testid="edit-profile-btn">
                  Complete Profile
                </Button>
              </>
            ) : (
              <div className="mt-5 space-y-3">
                <div><Label className="text-xs">Headline</Label><Input value={form.headline} onChange={(e) => setForm({ ...form, headline: e.target.value })} className="mt-1 h-8 text-sm" data-testid="profile-headline" /></div>
                <div><Label className="text-xs">Skills (comma separated)</Label><Input value={form.skills} onChange={(e) => setForm({ ...form, skills: e.target.value })} className="mt-1 h-8 text-sm" data-testid="profile-skills" /></div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label className="text-xs">Experience (yrs)</Label><Input type="number" value={form.experience_years} onChange={(e) => setForm({ ...form, experience_years: e.target.value })} className="mt-1 h-8 text-sm" /></div>
                  <div><Label className="text-xs">Location</Label><Input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} className="mt-1 h-8 text-sm" data-testid="profile-location" /></div>
                  <div><Label className="text-xs">Expected CTC (₹)</Label><Input type="number" value={form.expected_ctc} onChange={(e) => setForm({ ...form, expected_ctc: e.target.value })} className="mt-1 h-8 text-sm" /></div>
                  <div><Label className="text-xs">Notice period</Label><Input value={form.notice_period} onChange={(e) => setForm({ ...form, notice_period: e.target.value })} className="mt-1 h-8 text-sm" /></div>
                </div>
                <div><Label className="text-xs">Summary</Label><Textarea rows={3} value={form.summary} onChange={(e) => setForm({ ...form, summary: e.target.value })} className="mt-1 text-sm" /></div>
                <div className="flex gap-2">
                  <Button size="sm" className="rounded-full flex-1 bg-[#0B1B3D]" onClick={saveProfile} data-testid="save-profile-btn">Save</Button>
                  <Button size="sm" variant="ghost" onClick={() => setEditing(false)}>Cancel</Button>
                </div>
              </div>
            )}
          </div>

          <Link to="/candidate/cv">
            <div className="navy-bg rounded-xl p-6 text-white card-hover cursor-pointer" data-testid="cv-tools-card">
              <p className="font-heading font-semibold">CV & AI Tools</p>
              <p className="text-xs text-white/60 mt-2">AI CV score, interview preparation and keyword recommendations.</p>
            </div>
          </Link>

          <div className="bg-white border border-border/60 rounded-xl p-6" data-testid="saved-jobs-card">
            <h3 className="font-heading font-semibold text-[#0B1B3D] mb-4 flex items-center gap-2">
              <Bookmark className="w-4 h-4 text-[#F5A623]" /> Saved jobs
            </h3>
            {savedJobs.length === 0 ? (
              <p className="text-xs text-muted-foreground">Bookmark jobs while browsing to revisit them here.</p>
            ) : (
              <div className="space-y-2">
                {savedJobs.slice(0, 5).map((s) => (
                  <Link key={s.id} to={`/jobs/${s.job_id}`} className="block border border-border/60 rounded-lg p-3 hover:border-[#2563EB] transition-colors" data-testid={`saved-job-${s.id}`}>
                    <p className="text-sm font-medium">{s.job_title}</p>
                    <p className="text-xs text-muted-foreground">{s.company_name} · {s.location}</p>
                  </Link>
                ))}
              </div>
            )}
          </div>

          <div className="bg-white border border-border/60 rounded-xl p-6" data-testid="job-alerts-card">
            <h3 className="font-heading font-semibold text-[#0B1B3D] mb-4">Job alerts</h3>
            <div className="space-y-3">
              <div><Label className="text-xs">Keywords (comma separated)</Label><Input value={alerts.keywords} onChange={(e) => setAlerts({ ...alerts, keywords: e.target.value })} placeholder="Sales, CRM" className="mt-1 h-8 text-sm" data-testid="alert-keywords" /></div>
              <div><Label className="text-xs">Location</Label><Input value={alerts.location} onChange={(e) => setAlerts({ ...alerts, location: e.target.value })} placeholder="Bengaluru" className="mt-1 h-8 text-sm" data-testid="alert-location" /></div>
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={alerts.enabled} onChange={(e) => setAlerts({ ...alerts, enabled: e.target.checked })} className="accent-[#2563EB]" data-testid="alert-enabled" />
                Notify me about matching jobs (in-app + weekly email digest)
              </label>
              <Button size="sm" className="rounded-full w-full bg-[#0B1B3D]" onClick={async () => {
                try {
                  await api.put("/candidate/job-alerts", alerts);
                  toast.success("Job alert preferences saved");
                } catch (e) { toast.error(errMsg(e)); }
              }} data-testid="alert-save-btn">Save Alerts</Button>
            </div>
          </div>

          <NotificationPrefs />

          <div className="bg-white border border-border/60 rounded-xl p-6" data-testid="data-export-card">
            <h3 className="font-heading font-semibold text-[#0B1B3D] mb-2">Your data</h3>
            <p className="text-xs text-muted-foreground mb-4">Download everything Job Spring holds about you — profile, applications, documents, certificates.</p>
            <Button size="sm" variant="outline" className="rounded-full w-full" onClick={async () => {
              try {
                const res = await api.get("/me/export", { responseType: "blob" });
                const url = URL.createObjectURL(new Blob([res.data], { type: "application/json" }));
                const a = document.createElement("a");
                a.href = url;
                a.download = "jobspring_my_data.json";
                a.click();
                URL.revokeObjectURL(url);
                toast.success("Export downloaded");
              } catch (e) { toast.error(errMsg(e)); }
            }} data-testid="export-data-btn">
              Export My Data (JSON)
            </Button>
          </div>

          {docs.length > 0 && (
            <div className="bg-white border border-border/60 rounded-xl p-6" data-testid="my-documents-card">
              <h3 className="font-heading font-semibold text-[#0B1B3D] mb-4">Documents</h3>
              <div className="space-y-2">
                {docs.map((d) => (
                  <button
                    key={d.id}
                    onClick={async () => {
                      try {
                        const { data } = await api.get(`/documents/${d.id}`);
                        setViewDoc(data);
                      } catch (e) { toast.error(errMsg(e)); }
                    }}
                    className="w-full text-left flex items-center gap-3 border border-border/60 rounded-lg p-3 hover:border-[#2563EB] transition-colors"
                    data-testid={`my-doc-${d.id}`}
                  >
                    <FileText className="w-4 h-4 text-[#2563EB] shrink-0" />
                    <div>
                      <p className="text-sm font-medium">{d.template_name}</p>
                      <p className="text-xs text-muted-foreground">{d.company_name}</p>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      <Dialog open={!!viewDoc} onOpenChange={() => setViewDoc(null)}>
        <DialogContent className="max-w-2xl" data-testid="candidate-doc-dialog">
          <DialogHeader><DialogTitle>{viewDoc?.template_name}</DialogTitle></DialogHeader>
          <div className="bg-white border border-border rounded-lg p-6 whitespace-pre-line text-sm leading-relaxed font-serif max-h-[60vh] overflow-y-auto">
            {viewDoc?.body}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
